import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { listDecisions } from "../lib/api-modules/decisions.js";

/**
 * Dashboard widget — surfaces decisions whose `revisit_date` falls in
 * the next 7 days. Reads `GET /v1/decisions?revisit_within_days=7&status=active`.
 *
 * Renders nothing when no decisions are due — the dashboard stays
 * uncluttered. When something is due, presents an inline panel with
 * each decision's title + revisit date + a link out to the entity it
 * was anchored to (when set).
 */

const REVISIT_WINDOW_DAYS = 7;

export function DecisionsRevisitWidget() {
  const query = useQuery({
    queryKey: ["decisions", "revisit", REVISIT_WINDOW_DAYS] as const,
    queryFn: () =>
      listDecisions({
        revisitWithinDays: REVISIT_WINDOW_DAYS,
        status: "active",
      }),
  });

  // Empty state: render nothing — no widget, no error noise.
  if (query.isPending) return null;
  if (query.isError) return null;
  if (!query.data || query.data.decisions.length === 0) return null;

  return (
    <section
      aria-labelledby="decisions-revisit-heading"
      className="rounded-xl border border-[var(--color-warning)] bg-[var(--color-warning-soft)] p-5"
      data-testid="decisions-revisit-widget"
    >
      <h2
        id="decisions-revisit-heading"
        className="text-xs font-medium uppercase tracking-wide text-[var(--color-warning)]"
      >
        Decisions to revisit · {REVISIT_WINDOW_DAYS}d
      </h2>
      <ul className="mt-3 space-y-2">
        {query.data.decisions.map((d) => (
          <li
            key={d.decision_id}
            className="flex items-baseline justify-between gap-3 text-sm"
          >
            <span className="font-medium text-[var(--color-text)]">{d.title}</span>
            <span className="shrink-0 text-xs text-[var(--color-muted)]">
              due {d.revisit_date}
              {d.related_entity_type === "account" && d.related_entity_id && (
                <>
                  {" · "}
                  <Link
                    to="/accounts"
                    className="text-[var(--color-accent)] hover:underline"
                  >
                    Account
                  </Link>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
